import {queryOptions} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssueViewerSecondaryViewServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type IssueViewerSecondaryViewFeatures = {
  readonly hideClosedSubIssues?: boolean
}

/**
 * Signed Alive channels for this issue; optional because the backend key ships separately.
 * `subIssues` is keyed by child relay id -- a child's edits publish on its channel, not the parent's.
 */
export type IssueViewerLiveUpdateChannels = {
  readonly updated?: string | null
  readonly timeline?: string | null
  readonly state?: string | null
  readonly subIssues?: Readonly<Record<string, string>> | null
}

export type IssueViewerSecondaryViewResponse = {
  readonly repository: IssueViewerSecondaryViewServerQueryQuery['repository']
  readonly features?: IssueViewerSecondaryViewFeatures
  readonly channels?: IssueViewerLiveUpdateChannels
}

export function issueViewerSecondaryViewQueryKey(owner: string, repo: string, number: number) {
  // Deliberately excludes `markAsRead` so toggling it doesn't bust/duplicate the cache entry,
  // mirroring the Relay path's `excludeFromCacheKey: ['markAsRead']`.
  return ['issue-viewer', 'secondary-view', {owner, repo, number}] as const
}

type IssueViewerSecondaryViewQueryOptionsProps = {
  owner: string
  repo: string
  number: number
  markAsRead: boolean
  sendAnalyticsEvent: SendAnalyticsEventFunction
  preheat?: boolean
}

export function issueViewerSecondaryViewQueryOptions({
  owner,
  repo,
  number,
  markAsRead,
  sendAnalyticsEvent,
  preheat = false,
}: IssueViewerSecondaryViewQueryOptionsProps) {
  return queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- markAsRead triggers a side effect without identifying the cached resource
    queryKey: issueViewerSecondaryViewQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchIssueViewerSecondaryView(owner, repo, number, markAsRead, signal),
    staleTime: 0,
    gcTime: DEFAULT_RELAY_CACHE_TTL,
    meta: preheat ? {isPreload: true} : undefined,
    persister: createServerPersistedQueryPersister({
      queryHash: IssueViewerSecondaryViewServerQuery,
      sendAnalyticsEvent,
      maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
      alwaysRefetch: true,
    }),
  })
}

export async function fetchIssueViewerSecondaryView(
  owner: string,
  repo: string,
  number: number,
  markAsRead: boolean,
  signal?: AbortSignal,
): Promise<IssueViewerSecondaryViewResponse> {
  const response = await verifiedFetchJSON(
    `/${owner}/${repo}/issues/${number}/secondary_view?markAsRead=${markAsRead}&queryId=${IssueViewerSecondaryViewServerQuery}`,
    {signal, credentials: 'include'},
  )
  if (!response.ok) {
    throw new Error(`Issue viewer secondary view fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {
    data?: IssueViewerSecondaryViewServerQueryQuery
    errors?: unknown[]
    features?: IssueViewerSecondaryViewFeatures
    channels?: {
      updated?: string | null
      timeline?: string | null
      state?: string | null
      sub_issues?: Record<string, string> | null
    }
  }
  if (json.errors?.length || !json.data) {
    throw new Error('Issue viewer secondary view query returned errors')
  }

  return {
    repository: json.data.repository,
    features: json.features,
    channels: json.channels && {
      updated: json.channels.updated,
      timeline: json.channels.timeline,
      state: json.channels.state,
      subIssues: json.channels.sub_issues,
    },
  }
}
